// @mui material components
import Card from "@mui/material/Card";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftComponents/SoftBox";
import SoftTypography from "components/SoftComponents/SoftTypography";

import DevicesTable from "components/Tables/DevicesTable";

import {
    QueryClient,
    QueryClientProvider,
    useQuery,
} from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'

import bksAPI from 'services/api/bks';
import inspectionAPI from 'modules/inspections/api';
import { calculateBattery, processDevicesData } from "utils/device";


import { useNavigate } from 'react-router-dom';
import { useAuth0, withAuthenticationRequired } from "@auth0/auth0-react";

import PropTypes from 'prop-types';
import { useEffect } from "react";
import { Box, Container, Grid, Tooltip } from "@mui/material";
import moment from "moment";
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import UptimeDoughnutChart from "components/Charts/StatisticCards/UptimeDoughnutChart";

import { wrapper, loadingScreen } from "utils/wrapper";
import { disableMap } from "utils/map";
import { useSoftUIController } from "hooks/context";
import NewAssignmentsCard from "components/Cards/NewAssignmentsCard";
import ReportGenerationCard from "components/Cards/ReportGenerationCard";
import { getHealthStatus } from "utils/device";
import { createDataTableRows } from "utils/device";

function DevicesTableView({ map, user }) {
    const entriesPerPage = { defaultValue: 25, entries: [10, 25, 50, 75] };

    useEffect(() => {
        if (!map.current) return;
        disableMap(map);
    }, []);

    // Queries
    bksAPI.loadToken(useAuth0());
    inspectionAPI.loadToken(useAuth0());

    const navigate = useNavigate();
    const [controller] = useSoftUIController();
    const { deviceLayout, userPermissions } = controller;

    const canAssignmentsView = userPermissions.indexOf('operation-assignments:view') >= 0;
    const showAdvancedDeviceLevel = userPermissions.indexOf('devices-advanced:view') >= 0;

    const onRowClicked = function (row, event) {
        // console.log(row);
        // console.log(event);
        goToDevicePage(row.original.uuid);
    }

    const goToDevicePage = (uuid) => {
        let path = '../../devices/' + uuid;
        // console.log(navigate)
        navigate(path);
    }

    const devices = useQuery({
        queryKey: ['getAllDevices'],
        queryFn: async () => {
            return await bksAPI.getAllDevices()
        },
        refetchInterval: 300 * 1000,
        refetchIntervalInBackground: true,
    });

    const all_inspections = useQuery({
        queryKey: ['getAllInspectionsForUser', user],
        enabled: canAssignmentsView,
        queryFn: async () => {
            return await inspectionAPI.getAllInspectionsForUser(user);
        }
    });

    if (devices.isLoading || all_inspections.isLoading) {
        return loadingScreen(deviceLayout, true);
    }

    if (devices.error || all_inspections.error) {
        console.log(devices.error, all_inspections.error)
        return "An error has occurred";
    }

    const devicesData = processDevicesData(devices.data, showAdvancedDeviceLevel);
    const data_table_data = createDataTableRows(devicesData, showAdvancedDeviceLevel);

    const devicesTable = (
        <Card sx={{ height: '100%', width: "100%" }}>
            <SoftBox p={2} lineHeight={1}>
                <SoftBox display='flex' flexDirection='row' alignItems='flex-start'>
                    <SoftTypography px={1} variant="h4">
                        All Devices
                    </SoftTypography>
                    {showAdvancedDeviceLevel ? (
                        <Tooltip title={<div style={{ textAlign: 'left' }}>Red - Outdated/Offline<br />Yellow - Requires Attention<br />Green - Healthy</div>} placement="right" sx={{ paddingTop: '7px' }}>
                            <HelpOutlineIcon fontSize="small" />
                        </Tooltip>
                    ) : (
                        <Tooltip title={<div style={{ textAlign: 'left' }}>Red - Requires Attention<br />Green - Healthy</div>} placement="right" sx={{ paddingTop: '7px' }}>
                            <HelpOutlineIcon fontSize="small" />
                        </Tooltip>
                    )}
                </SoftBox>
                <DevicesTable table={data_table_data} entriesPerPage={entriesPerPage} showAdvancedDeviceLevel={showAdvancedDeviceLevel} onRowClicked={(row, event) => onRowClicked(row, event)} canSearch />
            </SoftBox>
        </Card>
    );
    
    return wrapper((
        <Grid container spacing={deviceLayout == 'mobile' ? 1 : 2}>
            {canAssignmentsView ? (
                <>
                    <Grid item xxs={12} md={6} xl={6}>
                        <NewAssignmentsCard assignment_count={all_inspections.data.inspections.filter((row) => row.status == "unresolved" || row.status == "review").length} />
                    </Grid>
                    <Grid item xxs={12} md={6} xl={6}>
                        <UptimeDoughnutChart
                            icon="percent"
                            color="secondary"
                            title="All Devices"
                            devices={devices.data}
                            height={deviceLayout == 'mobile' ? '100px' : 'unset'}
                            maxHeight='180px'
                        />
                    </Grid>
                </>
            ) : (
                <>
                    <Grid item xxs={12} md={12}>
                        <UptimeDoughnutChart
                            icon="percent"
                            color="secondary"
                            title="All Active Devices"
                            devices={devices.data}
                            height={deviceLayout == 'mobile' ? '100px' : 'unset'}
                            maxHeight='180px'
                        />
                    </Grid>
                </>
            )}
            <Grid item xxs={12}>
                {devicesTable}
            </Grid>
        </Grid >
    ), deviceLayout);
}

DevicesTableView.propTypes = {
    map: PropTypes.object,
    user: PropTypes.object
}

export default DevicesTableView;
